import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, Download, File, FileImage, FileText, Folder, FolderOpen, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useClients } from "@/lib/hooks";
import { safeUrl } from "@/lib/utils";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SOURCE_LABELS = { CLIENT: "Cliente", PROJECT: "Projeto", TASK: "Tarefa", EVENT: "Evento" };
const NO_CLIENT = "__sem_cliente__";

function sourceLink(asset) {
  if (asset.sourceType === "CLIENT") return `/clientes/${asset.sourceId}`;
  if (asset.sourceType === "PROJECT") return `/projetos/${asset.sourceId}`;
  if (asset.sourceType === "EVENT") return "/agenda";
  return "/workspace";
}

function FileIcon({ asset }) {
  const type = (asset.format || "").toLowerCase();
  if (type.includes("image") || /png|jpg|jpeg|webp|svg/.test(type)) return <FileImage className="h-6 w-6" />;
  if (type.includes("pdf") || type.includes("text")) return <FileText className="h-6 w-6" />;
  return <File className="h-6 w-6" />;
}

function AssetCard({ asset }) {
  const isImage = (asset.format || "").toLowerCase().includes("image") && asset.dataUrl;
  return (
    <Card className="group overflow-hidden border-border">
      <div className="flex h-28 items-center justify-center bg-muted/30 text-primary">
        {isImage ? <img src={asset.dataUrl} alt="" className="h-full w-full object-cover" /> : <FileIcon asset={asset} />}
      </div>
      <div className="p-4">
        <div className="truncate text-sm font-semibold" title={asset.name}>{asset.name}</div>
        <div className="mt-1 flex items-center gap-2">
          <Badge variant="secondary" className="rounded-full text-[10px]">{SOURCE_LABELS[asset.sourceType]}</Badge>
          <span className="truncate text-[10px] text-muted-foreground">{asset.sourceName}</span>
        </div>
        <div className="mt-3 flex gap-2">
          <Button asChild size="sm" variant="outline" className="flex-1">
            <Link to={sourceLink(asset)}><FolderOpen className="mr-1 h-3.5 w-3.5" /> Abrir origem</Link>
          </Button>
          <Button asChild size="icon" variant="outline">
            <a href={safeUrl(asset.dataUrl, "#")} download={asset.name} aria-label="Descarregar"><Download className="h-4 w-4" /></a>
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function Assets() {
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: async () => (await api.get("/assets")).data });
  const { data: clients = [] } = useClients();
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [openFolder, setOpenFolder] = useState(null);

  const matching = useMemo(() => assets.filter((asset) => {
    if (source !== "all" && asset.sourceType !== source) return false;
    return `${asset.name} ${asset.sourceName} ${asset.clientName}`.toLowerCase().includes(search.toLowerCase());
  }), [assets, source, search]);

  // Uma pasta por cliente, mais uma para o que não está ligado a nenhum.
  const folders = useMemo(() => {
    const byClient = new Map();
    matching.forEach((asset) => {
      const key = asset.clientId || NO_CLIENT;
      if (!byClient.has(key)) {
        byClient.set(key, {
          id: key,
          name: asset.clientName || "Sem cliente",
          files: [],
        });
      }
      byClient.get(key).files.push(asset);
    });
    // Clientes sem ficheiros também aparecem, para a pasta existir à partida.
    clients.forEach((client) => {
      if (!byClient.has(client.id)) byClient.set(client.id, { id: client.id, name: client.name, files: [] });
    });
    return [...byClient.values()].sort((first, second) => {
      if (first.id === NO_CLIENT) return 1;
      if (second.id === NO_CLIENT) return -1;
      return first.name.localeCompare(second.name, "pt");
    });
  }, [matching, clients]);

  const current = openFolder ? folders.find((folder) => folder.id === openFolder) : null;
  const searching = search.trim().length > 0;
  // A pesquisar, mostra logo os ficheiros de todas as pastas.
  const showFiles = Boolean(current) || searching;
  const visibleFiles = current ? current.files : matching;

  return (
    <div>
      <PageHeader
        title="Biblioteca"
        subtitle="Ficheiros de clientes, projetos, tarefas e eventos, organizados por cliente."
      />

      <Card className="mb-5 flex flex-col gap-3 p-3 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Pesquisar ficheiros ou origem"
            className="pl-9"
          />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="md:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as origens</SelectItem>
            {Object.entries(SOURCE_LABELS).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {current && (
        <div className="mb-4 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpenFolder(null)} data-testid="assets-back">
            <ChevronLeft className="mr-1 h-4 w-4" /> Pastas
          </Button>
          <span className="font-poppins font-semibold">{current.name}</span>
          <span className="text-xs text-muted-foreground">{current.files.length} ficheiro(s)</span>
        </div>
      )}

      {!showFiles && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="assets-folders">
          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              onClick={() => setOpenFolder(folder.id)}
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/50"
              data-testid={`assets-folder-${folder.id}`}
            >
              <Folder className="h-8 w-8 shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="truncate font-poppins text-sm font-semibold">{folder.name}</div>
                <div className="text-xs text-muted-foreground">
                  {folder.files.length === 1 ? "1 ficheiro" : `${folder.files.length} ficheiros`}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showFiles && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleFiles.map((asset, index) => (
            <AssetCard key={`${asset.sourceType}-${asset.sourceId}-${asset.id}-${index}`} asset={asset} />
          ))}
        </div>
      )}

      {showFiles && visibleFiles.length === 0 && (
        <Card className="border-dashed p-12 text-center">
          <FolderOpen className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            {current ? "Esta pasta ainda não tem ficheiros." : "Nenhum ficheiro corresponde à pesquisa."}
          </p>
        </Card>
      )}
    </div>
  );
}
